import { ChangeEvent, useId, useRef } from 'react';
import { PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

type ProductImageFieldProps = {
  label: string;
  hint: string;
  value: string;
  variant: 'logo' | 'cover';
  onChange: (dataUrl: string) => void;
  onClear: () => void;
};

export function ProductImageField({
  label,
  hint,
  value,
  variant,
  onChange,
  onClear,
}: ProductImageFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const isLogo = variant === 'logo';

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(new Error('Falha ao ler arquivo.'));
        reader.readAsDataURL(file);
      });
      onChange(dataUrl);
    } catch {
      toast.error('Falha ao carregar imagem.');
    } finally {
      event.target.value = '';
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={inputId} className="text-sm font-medium text-secondary-800">
          {label}
        </label>
        {value ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
            onClick={onClear}
          >
            <TrashIcon className="h-3.5 w-3.5" aria-hidden />
            Remover
          </button>
        ) : null}
      </div>

      <div
        className={`overflow-hidden rounded-xl border border-dashed border-secondary-300 bg-secondary-50/60 ${
          isLogo ? 'aspect-square max-w-[11rem]' : 'aspect-[16/9]'
        }`}
      >
        {value ? (
          <button
            type="button"
            className="group relative block h-full w-full"
            onClick={() => inputRef.current?.click()}
            title="Trocar imagem"
          >
            <img
              src={value}
              alt={label}
              className={`h-full w-full ${isLogo ? 'object-contain bg-white p-4' : 'object-cover'}`}
            />
            <span className="absolute inset-0 flex items-center justify-center bg-secondary-950/0 text-sm font-medium text-white opacity-0 transition group-hover:bg-secondary-950/45 group-hover:opacity-100">
              Trocar imagem
            </span>
          </button>
        ) : (
          <label
            htmlFor={inputId}
            className="flex h-full min-h-[8.5rem] cursor-pointer flex-col items-center justify-center gap-2 px-4 py-6 text-center transition hover:bg-secondary-100/80"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-secondary-500 shadow-sm ring-1 ring-secondary-200">
              <PhotoIcon className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-sm font-medium text-secondary-800">Selecionar imagem</span>
            <span className="text-xs text-secondary-500">{hint}</span>
          </label>
        )}
      </div>

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => void handleFileChange(event)}
      />
    </div>
  );
}
